import { Identity } from '../components/Identity';
import { DOMElement } from '../utils/DOMElement';
import { Normalizer } from '../utils/Normalizer';
import { WordingService } from '../services/WordingService';
import { PasswordError } from './PasswordError';
import { Validator } from '../validators/Validator';

/**
 * Identity Error Component.
 */
export class IdentityError {
  /**
   * FirstName Error.
   *
   * @type {string}
   */
  static firstNameErrorSelector = '#eui-firstName-error';

  /**
   * LastName Error.
   *
   * @type {string}
   */
  static lastNameErrorSelector = '#eui-lastName-error';

  /**
   * Civility Error.
   *
   * @type {string}
   */
  static civilityErrorSelector = '#eui-civility-error';

  /**
   * CGU Error.
   *
   * @type {string}
   */
  static cguErrorSelector = '#eui-cgu-error';

  /**
   * Civility Error.
   *
   * @type {string}
   */
  static emailErrorSelector = '#eui-email-error';

  /**
   * CSS Class in order to highlight the fact that the input has an error.
   *
   * @type {string}
   */
  static classInputError = 'is-invalid';

  /**
   * CSS Class in order to hide or display the error of civility.
   */
  static obi1ErrorClass = 'hide';

  /**
   * Constructor.
   */
  constructor() {
    this.errors = {
      lastName: {
        empty: WordingService.getWording('emptyLastName'),
        invalid: WordingService.getWording('invalidLastName'),
      },
      firstName: {
        empty: WordingService.getWording('emptyFirstName'),
        invalid: WordingService.getWording('invalidFirstName'),
      },
      civility: {
        empty: WordingService.getWording('emptyCivility'),
      },
      email: {
        empty: WordingService.getWording('emailErrorEmpty'),
        invalid: WordingService.getWording('emailErrorInvalid'),
        tooShort: WordingService.getWording('emailErrorTooShort'),
        tooLong: WordingService.getWording('emailErrorTooLong'),
        missingAt: WordingService.getWording('emailErrorMissingAt'),
        missingExtension: WordingService.getWording('emailErrorMissingExtension'),
        specialChars: WordingService.getWording('emailErrorSpecialChars'),
        accentChars: WordingService.getWording('emailErrorAccentChars'),
      },
    };
    this.passwordError = new PasswordError();
  }

  /**
   * Handle Error display for firstName.
   *
   * @param   {boolean} isValid - Is valid firstName.
   * @param   {string} value - Value of the firstName.
   * @param   {boolean} emptyCheck - If the empty property need to be checked.
   */
  handleErrorFirstName(isValid, value, emptyCheck) {
    if (isValid || (!emptyCheck && !value)) {
      this.hideError(Identity.firstNameDOMSelector);
    } else {
      this.displayErrorFirstName(value);
    }
  }

  /**
   * Handle Error display for lastName.
   *
   * @param   {boolean} isValid - Is valid lastName.
   * @param   {string} value - Value of the lastName.
   * @param   {boolean} emptyCheck - If the empty property need to be checked.
   */
  handleErrorLastName(isValid, value, emptyCheck) {
    if (isValid || (!emptyCheck && !value)) {
      this.hideError(Identity.lastNameDOMSelector);
    } else {
      this.displayErrorLastName(value);
    }
  }

  /**
   * Handle Error display for Civility.
   *
   * @param   {boolean} isValid - Is valid civility.
   */
  handleErrorCivility(isValid) {
    const civilityChoices = document.querySelectorAll(Identity.civilityDOMSelector);

    if (isValid) {
      DOMElement.addClass(IdentityError.obi1ErrorClass, document.querySelector(IdentityError.civilityErrorSelector));
      civilityChoices.forEach((civility) => {
        DOMElement.removeClass(IdentityError.classInputError, civility);
        civility.setAttribute('aria-invalid', 'false');
      });
    } else {
      DOMElement.removeClass(IdentityError.obi1ErrorClass, document.querySelector(IdentityError.civilityErrorSelector));
      civilityChoices.forEach((civility) => {
        DOMElement.addClass(IdentityError.classInputError, civility);
        civility.setAttribute('aria-invalid', 'true');
      });
    }
  }

  /**
   * Handle Error for CGU.
   *
   * @param {boolean} isValid - If the cgu are checked or not.
   */
  handleErrorCGU(isValid) {
    if (isValid) {
      DOMElement.addClass(IdentityError.obi1ErrorClass, document.querySelector(IdentityError.cguErrorSelector));
      DOMElement.removeClass(IdentityError.classInputError, document.querySelector(Identity.cguDOMSelector));
      document.querySelector(Identity.cguDOMSelector).setAttribute('aria-invalid', 'false');
    } else {
      DOMElement.addClass(IdentityError.classInputError, document.querySelector(Identity.cguDOMSelector));
      DOMElement.removeClass(IdentityError.obi1ErrorClass, document.querySelector(IdentityError.cguErrorSelector));
      document.querySelector(Identity.cguDOMSelector).setAttribute('aria-invalid', 'true');
    }
  }

  /**
   * Display Error for firstName.
   *
   * @param   {string} value - Value of the firstName.
   */
  displayErrorFirstName(value) {
    let errorMsg = '';

    if (!value) {
      errorMsg = this.errors.firstName.empty;
    } else {
      errorMsg = this.errors.firstName.invalid;
    }

    this.displayError(IdentityError.firstNameErrorSelector, Identity.firstNameDOMSelector, errorMsg);
  }

  /**
   * Display Error for lastName.
   *
   * @param   {string} value - Value of the lastName.
   */
  displayErrorLastName(value) {
    let errorMsg = '';

    if (!value) {
      errorMsg = this.errors.lastName.empty;
    } else {
      errorMsg = this.errors.lastName.invalid;
    }

    this.displayError(IdentityError.lastNameErrorSelector, Identity.lastNameDOMSelector, errorMsg);
  }

  /**
   * Display error for email.
   *
   * @param {string} value - Value of the email.
   */
  displayErrorEmail(value) {
    let errorMsg;
    if (!value) {
      errorMsg = this.errors.email.empty;
    } else {
      const errorStruct = this.checkEmailStructure(value);
      const errorChars = this.checkEmailCharacters(value);
      if (!!errorStruct === !!errorChars) {
        errorMsg = this.errors.email.invalid;
      } else {
        errorMsg = errorStruct || errorChars;
      }
    }
    this.displayError(IdentityError.emailErrorSelector, Identity.emailDOMSelector, errorMsg);
  }

  checkEmailStructure(email) {
    const [local, domain] = email.split('@');
    // check minimum length
    if (email.length < Validator.emailMinLength) {
      return this.errors.email.tooShort;
    // check maximum length
    } if (email.length > Validator.emailMaxLength) {
      return this.errors.email.tooLong;
    // check domain existence
    } if (email.indexOf('@') === -1) {
      return this.errors.email.missingAt;
    }
    // check dot rules, avoid RegExp fatigue.
    // Allow to get specific behaviour for this rule
    if (local.indexOf('.') === 0
      || local.indexOf('.') === local.length - 1
      || local.indexOf('..') !== -1) {
      return this.errors.email.invalid;
    }
    // check extension existence
    if (domain.indexOf('.') < 1
      || !(domain.charAt(domain.indexOf('.') - 1).match(/^[a-zA-Z]$/))
      || !(domain.charAt(domain.indexOf('.') + 1).match(/^[a-zA-Z]$/))) {
      return this.errors.email.missingExtension;
    }

    return false;
  }

  checkEmailCharacters(value) {
    let errorMsg = false;
    const normalizedValue = Normalizer(value);
    // check accents
    if (value !== normalizedValue) {
      errorMsg = this.errors.email.accentChars;
    }
    // check special chars not allowed
    if (!normalizedValue.match(/^[a-zA-Z0-9.\-_@]+$/)) {
      errorMsg = this.errors.email.specialChars;
    }
    return errorMsg;
  }

  /**
   * Show the password error messages.
   */
  showPasswordMessages() {
    this.passwordError.showMessages();
  }

  /**
   * Update all password error related fields and values.
   *
   * @param {obj} passwordStatuses - All statuses of the password.
   * @param {bool} isOnBlur - Whether the update is triggered by an onblur event.
   */
  updatePassword(passwordStatuses, isOnBlur) {
    this.passwordError.update(passwordStatuses, isOnBlur);
  }

  /**
   * Show the password error stating that it is the same as the email.
   */
  setEmailSameAsPasswordError() {
    this.passwordError.setSameAsLoginError();
  }

  /**
   * Hide the password error messages.
   *
   * @param {bool} isPasswordEmpty - True if the password is empty, false otherwise.
   */
  hidePasswordMessages(isPasswordEmpty) {
    this.passwordError.hideMessages(isPasswordEmpty);
  }

  /**
   * Handle error display for email.
   *
   * @param {boolean} isValid - Is valid email.
   * @param {string}  value -   - Value of the email.
   * @param {boolean} emptyCheck - If the empty property need to be checked.
   */
  handleErrorEmail(isValid, value, emptyCheck) {
    if (isValid || (!emptyCheck && !value)) {
      this.hideError(Identity.emailDOMSelector);
    } else {
      this.displayErrorEmail(value);
    }
  }

  /**
   * Display error.
   *
   * @param   {object} errorSelector - Dom selector.
   * @param   {string} inputSelector - The input error.
   * @param   {string} errorMsg -  - ErrorToDisplay.
   */
  displayError(errorSelector, inputSelector, errorMsg) {
    const errorNodes = document.querySelectorAll(errorSelector);
    const inputNodes = document.querySelectorAll(inputSelector);

    for (let i = 0; i < errorNodes.length; i += 1) {
      const errorNode = errorNodes[i];

      if (errorMsg && errorNode) {
        errorNode.innerHTML = errorMsg;
      }
    }

    for (let i = 0; i < inputNodes.length; i += 1) {
      const inputNode = inputNodes[i];

      DOMElement.addClass(IdentityError.classInputError, inputNode);
      inputNode.setAttribute('aria-invalid', 'true');
    }
  }

  /**
   * Hide error.
   *
   * @param   {string} inputSelector - The message error.
   */
  hideError(inputSelector) {
    const inputNodes = document.querySelectorAll(inputSelector);

    // remove css class to remove input highlighting
    for (let i = 0; i < inputNodes.length; i += 1) {
      const inputNode = inputNodes[i];

      DOMElement.removeClass(IdentityError.classInputError, inputNode);
      inputNode.setAttribute('aria-invalid', 'false');
    }
  }
}
